import { mapActions, mapState } from 'vuex'
import flowModule from '@/modules/Flow/store'
import {
  FLOW_MODULE_NAME,
  FLOW_SET_NAME,
  FLOW_SET_E2EFLOW,
  FLOW_SET_STATE_NAME,
  FLOW_SET_STATE_TRANSITIONS,
  FLOW_SET_STATE_VIEW_CONTENT,
  FLOW_SET_STATE_VIEW_OPTIONS,
  FLOW_SET_COMPANY_ICON,
  FLOW_SET_COMPANY_COLOR, FLOW_SET_COMPANY
} from '@/modules/Flow/store/types'
import { USER_MODULE_NAME } from '@/modules/User/store/types'
import {
  WARRANTY_MODULE_NAME,
  FNOL_MODULE_NAME,
  UPDATE_LOADER
} from '@/store/types'
import {
  apiGetFlowData,
  apiGetTransitionToNextState
} from '@/modules/Flow/services/flow'
import { flowStates } from '@/modules/Flow/config/flow'
import setTimeStamp from '@/utils/timeStamp.js'
import { apiPostTimeStamp } from '@/modules/User/services/timeStamp'
import { FLOW_NAMES, FLOW_TRANSITIONS, OTHERS } from '@/modules/Flow/constants'

import maEventBus from '@/components/eventbus/index.js'
import * as Sentry from '@sentry/vue'

const isValidState = (stateName) => {
  const selectedState = flowStates.filter(
    (state) => state.name === stateName
  )[0]
  return selectedState
}

const SENTRY_ERROR_LEVELS = {
  fatal: 'fatal',
  error: 'error',
  warning: 'warning',
  log: 'log',
  info: 'info',
  debug: 'debug'
}

export default {
  data: function() {
    return {
      stateIsLoading: false
    }
  },

  computed: {
    ...mapState(WARRANTY_MODULE_NAME, {
      stateWarrantySinisterId: 'sinisterId',
      stateWarrantyId: 'warrantyId',
      stateWarrantyPropertyType: 'propertyType',
      stateWarrantyWarranties: 'warranties'
    }),

    ...mapState(USER_MODULE_NAME, {
      stateUserId: 'userId'
    }),

    ...mapState(FNOL_MODULE_NAME, {
      stateIdFlux: 'idFlux'
    }),

    ...mapState(FLOW_MODULE_NAME, {
      stateFlowName: 'flowName',
      stateE2EFlow: 'e2eFlow',
      stateFlowCompanyIcon: 'flowCompanyIcon',
      stateFlowState: 'flowState',
      stateFlowTransitions: 'flowTransitions',
      stateFlowViewContent: 'flowViewContent',
      stateFlowViewOptions: 'flowViewOptions',
      stateFlowCompanyColor: 'flowCompanyColor',
      stateFlowCompany: 'flowCompany'
    }),

    getSinisterId() {
      return (
        this.stateWarrantySinisterId ||
                this.$route.query.sinisterId ||
                sessionStorage.getItem('sinId')
      )
    },

    getUser() {
      return this.stateUserId || this.$route.params.user
    },

    getWarrantyId() {
      return (
        this.stateWarrantyId ||
                this.$route.params.warrantyId ||
                sessionStorage.getItem('formId')
      )
    },

    getFnolId() {
      return this.stateIdFlux
    },

    getPropertyType() {
      return this.stateWarrantyPropertyType || this.$route.params.propertyType
    },

    getDamageType() {
      return this.$route.params.damageType
    },

    getDamagedItem() {
      return this.$route.params.damagedItem
    },

    getProductId() {
      return this.$route.params.productId
    },

    getDamageName() {
      return this.$route.params.name
    },

    getFlowName() {
      return this.stateFlowName || null
    },

    getE2EFlow() {
      return this.stateE2EFlow || null
    },

    getFlowState() {
      return this.stateFlowState || null
    },

    getFlowTransitions() {
      return this.stateFlowTransitions || null
    },

    getFlowViewContent() {
      return this.stateFlowViewContent || null
    },

    getFlowViewOptions() {
      return this.stateFlowViewOptions || null
    },

    getFlowStateViewName() {
      const funtionalityState = flowStates.filter(
        (functionality) => functionality.name === this.getFlowState
      )[0]
      return funtionalityState ? funtionalityState.entryPointViewName : null
    },

    isE2EFlow() {
      return this.getFlowName === FLOW_NAMES.E2E
    },

    isBillmaBPCEFlow() {
      return this.getFlowName === FLOW_NAMES.BPCE
    },

    hasMobilierTransition() {
      return this.getFlowTransitions.includes(FLOW_TRANSITIONS.SHOW_MOBILIER)
    },

    // STATE OPTIONS
    addDamagesHasImages() {
      if (this.getDamageType !== OTHERS) {
        return this.getFlowViewOptions
          ? this.getFlowViewOptions.imagesRequired
          : false
      }
    },

    getPhotoclaimForms() {
      return this.getFlowViewOptions
        ? this.getFlowViewOptions.photoclaimForms
        : null
    },

    getFlowCompanyIcon() {
      return this.stateFlowCompanyIcon
    },

    getFlowCompanyColor() {
      return String(this.stateFlowCompanyColor) || '#005BA5'
    },

    getFlowCompany() {
      return this.stateFlowCompany
    }
  },
  methods: {
    ...mapActions(FLOW_MODULE_NAME, {
      actionFlowSetName: FLOW_SET_NAME,
      actionFlowSetE2EFlow: FLOW_SET_E2EFLOW,
      actionFlowSetCompanyIcon: FLOW_SET_COMPANY_ICON,
      actionFlowSetState: FLOW_SET_STATE_NAME,
      actionFlowSetTransitions: FLOW_SET_STATE_TRANSITIONS,
      actionFlowSetViewContent: FLOW_SET_STATE_VIEW_CONTENT,
      actionFlowSetViewOptions: FLOW_SET_STATE_VIEW_OPTIONS,
      actionFlowSetCompany: FLOW_SET_COMPANY,
      actionFlowSetCompanyColor: FLOW_SET_COMPANY_COLOR
    }),

    setFlowName(flowName) {
      this.actionFlowSetName(flowName)
    },

    setFlowE2EFlow(e2eFlow) {
      this.actionFlowSetE2EFlow(e2eFlow)
    },

    setFlowCompanyIcon(iconUrl) {
      this.actionFlowSetCompanyIcon(iconUrl)
    },
    setFlowCompanyColor(color) {
      this.actionFlowSetCompanyColor(color)
    },
    setFlowCompany(company) {
      this.actionFlowSetCompany(company)
    },

    setFlowState(stateName, stateTransitions) {
      if (!stateName) return

      if (isValidState(stateName)) {
        this.actionFlowSetState(stateName)
        this.actionFlowSetTransitions(stateTransitions)
      } else {
        this.sendSentryEvent(
          `${stateName} is not a valid state`,
          this.setFlowState.name
        )
        this.goToErrorView()
      }
    },

    async getStateFlowData() {
      try {
        const stateData = await apiGetFlowData(sessionStorage.getItem('sinId'))
        const { options } = stateData

        this.setViewOptions(options)
        return stateData
      } catch (e) {
        this.sendSentryEvent(e, this.getStateFlowData.name, null, e)
        this.goToErrorView()
        throw new Error(e)
      }
    },

    async getTransitionToNextState(transitionEvent, eventOptions = null) {
      try {
        const nextStateData = await apiGetTransitionToNextState(
          sessionStorage.getItem('sinId'),
          transitionEvent,
          eventOptions
        )
        return nextStateData
      } catch (e) {
        this.sendSentryEvent(e, this.getTransitionToNextState.name, null, e)
        this.goToErrorView()
        throw new Error(e)
      }
    },

    goToView(viewName, viewPathParams) {
      this.$router
        .push({
          name: viewName,
          params: {
            user: this.getUser,
            sinisterId: this.getSinisterId,
            warrantyId: this.getWarrantyId,
            ...viewPathParams
          }
        })
        .catch((e) => {})
    },

    async goToNextState(event = null, eventOptions = null) {
      const appViewTransitionDuration = 600

      if (this.stateIsLoading) return
      this.handleLoadingState(true)

      const nextTransition = event || this.getFlowTransitions[0] || null
      const nextStateData = await this.getTransitionToNextState(
        nextTransition,
        eventOptions
      )

      const { state, transitions, options, e2eFlow } = nextStateData
      this.goToStateView(state, transitions)
      if (options.content) this.setViewContent(options.content)
      this.setViewOptions(options)
      this.setFlowE2EFlow(e2eFlow)

      setTimeout(() => {
        this.handleLoadingState(false)
      }, appViewTransitionDuration)
    },

    handleLoadingState(stateIsLoading) {
      this.stateIsLoading = stateIsLoading
      this.$store.commit(UPDATE_LOADER, stateIsLoading)
    },

    goToStateView(stateName, stateTransitions) {
      this.setFlowState(stateName, stateTransitions)
      this.goToView(this.getFlowStateViewName)
    },

    goToErrorView(query = null) {
      const name = 'error'

      if (this.$route.name !== name) {
        this.$router.replace({ name, query })
      }
    },

    goBack() {
      const path = this.$router.navigationHistory.pop() || 'damages-view'
      const propertyType = this.getPropertyType
      this.goToView(path, { propertyType })
      this.$router.navigationHistory.pop()
    },

    trackUserFlowTimeStamp(message, sinisterId) {
      const timeStamp = setTimeStamp()

      const timeStampParams = {
        description: message,
        sinister_id: sinisterId,
        start: timeStamp,
        end: timeStamp
      }

      apiPostTimeStamp(timeStampParams).catch((err) => {
        const errs = [400, 500, 409, 404]
        const errCode =
                    errs.find((errCode) => err.response.status === errCode) || 0
        this.throwNotification(
          this.$t(`notifications.user.error.${errCode}.title`),
          this.$t(`notifications.user.error.${errCode}.body`),
          'KO'
        )
      })
    },

    resetFlowState() {
      this.setFlowState(null)
    },

    setViewContent(viewContent) {
      viewContent && viewContent.length > 0
        ? this.actionFlowSetViewContent(viewContent)
        : this.actionFlowSetViewContent([])
    },

    setViewOptions(options) {
      options
        ? this.actionFlowSetViewOptions(options)
        : this.actionFlowSetViewOptions({})
    },

    sendSentryMessage(component, message, errors) {
      const messageData = {
        level: 'info',
        extra: {
          activeComponent: component
        }
      }
      // Check if errors is comming
      if (errors.length > 0) messageData.extra.errors = [...errors]
      Sentry.captureMessage(message, messageData)
    },

    sendSentryError(
      message,
      level = null,
      tags = {},
      sinisterId = null,
      method = null,
      errorDetail = null,
      userId = null,
      routePath = null,
      routeName = null
    ) {
      const eventData = {
        extra: {}
      }

      // Set error level if found in ENUM CONST
      if (SENTRY_ERROR_LEVELS[level]) eventData.level = SENTRY_ERROR_LEVELS[level]
      // Set tags if any
      if (Object.entries(tags).length > 0) eventData.tags = { ...tags }
      // Set extra data
      // If sinisterId is passed
      if (sinisterId) eventData.extra.sinisterId = sinisterId
      // If method is passed
      if (method) eventData.extra.method = method
      // if errorDetail is passed
      if (errorDetail) eventData.extra.errorDetail = errorDetail
      // if userId is passed
      if (userId) eventData.extra.userId = userId
      // If routePath is passed
      if (routePath) eventData.extra.routePath = routePath
      // If routeName is passed
      if (routeName) eventData.extra.routeName = routeName
      // Send sentry event
      Sentry.captureException(new Error(message), eventData)
    },
    setGlobalObject () {
      if (typeof global !== 'undefined') return global
      if (typeof window !== 'undefined') return window
      if (typeof self !== 'undefined') return self
      return {}
    },
    sendSentryEvent(
      errorMessage = '',
      callerName = null,
      extraData = null,
      error = null
    ) {
      const sinisterId = this.getSinisterId || null
      // component who is currently active
      const activeComponent = extraData
        ? extraData.activeComponent || this.$options.name
        : this.$options.name
      // method who calls the Event
      callerName = callerName
        ? callerName.split(' ')[1]
        : `${activeComponent}Default`
      // Active route
      const activeRoute = this.$route.fullPath

      Sentry.withScope((scope) => {
        if (error) {
          // To track fatal error level
          const fatalErrors = ['TypeError', 'SyntaxError', 'ReferenceError', 'InternalError']
          // Check if error is fatal level
          // Check if the caught error is an instance of any fatal error type
          const globalObject = this.setGlobalObject()

          const isFatalError = fatalErrors.some((fatalErrorType) => Object.prototype.hasOwnProperty.call(globalObject, fatalErrorType) && error instanceof globalObject[fatalErrorType])
          if (isFatalError) scope.setLevel('fatal')
          // If is an HTTP error
          else if (error.response) {
            // Set tags
            scope.setTag('error.type', 'HttpError')
            scope.setExtra('http.status_code', error.response.status)
            // If status code is 500, set "error" level
            error.response.status >= 500 && error.response.status < 600 ? scope.setLevel('error') : scope.setLevel('warning')
          } else {
            scope.setTag('error.type', 'UncontrolledError')
            scope.setLevel('log')
          }
        }
        scope.setTag('sinisterId', sinisterId)
        scope.setTag('activeComponent', activeComponent)
        scope.setTag('callerName', callerName)
        scope.setTag('activeRoute', activeRoute.fullPath)

        if (extraData && extraData.hasUserInteracted !== undefined) scope.setTag('hasUserInteracted', extraData.hasUserInteracted)

        const context = {
          sinisterId,
          activeComponent,
          callerName,
          activeRoute,
          errorMessage,
          ...extraData
        }

        scope.setContext('data', context)

        Sentry.captureException(
          new Error(`[${activeComponent}.${callerName}] ${errorMessage}`)
        )
      })
    },

    notifyAppOfChanges(component) {
      maEventBus.$emit('setActiveComponent', component)
      maEventBus.$emit('reinitializeUserInteraction')
    },

    isNumber(value) {
      return /^\d+([,.]\d+)?$/.test(value)
    }

    // todo set mavalueHasImages
    // set flowResult
  },
  beforeCreate: function() {
    if (!(FLOW_MODULE_NAME in this.$store.state)) {
      this.$store.registerModule(FLOW_MODULE_NAME, flowModule)
    }
  }
}
